import { gql } from "@apollo/client";
import {
  AtlasAvatarGroupProps,
  AvatarGroup,
  useAvatarGroupItems,
} from "@resource/atlas";
import { strings } from "@resource/common";
import { formatPersonName } from "client/utils/strings";
import clsx from "clsx";
import { ModalName, useModals } from "components/ModalProvider";
import StopPropagation from "components/StopPropagation";
import { InterviewItemCardInterviewerFragment } from "generated/schemaTypes";
import { useMemo, useState } from "react";
import { useResponsiveValue } from "utils/responsive";

import { InterviewerDetailsDialog } from "../InterviewerDetailsDialog";

const INTERVIEW_ITEM_INTERVIEWER_FRAGMENT = gql`
  fragment InterviewItemCardInterviewerFragment on Interviewer {
    id
    firstName: displayFirstName
    lastName: displayLastName
    imageUrl: displayImageUrl
    ...InterviewerDetailsDialogInterviewerFragment
  }
  ${InterviewerDetailsDialog.fragments.interviewer}
`;
const getInterviewerDetailsDialogId = (id: string) =>
  `interview-item-card-interviewer-${id}`;
export function InterviewersSection({
  interviewers,
  isInPast,
}: {
  interviewers: InterviewItemCardInterviewerFragment[];
  isInPast: boolean;
}) {
  const modalManager = useModals();
  const [selectedInterviewer, setSelectedInterviewer] = useState<
    InterviewItemCardInterviewerFragment | undefined
  >();
  const avatarSize = useResponsiveValue<AtlasAvatarGroupProps["size"]>({
    xs: "small",
    md: "medium",
  });
  const interviewerAvatars = useAvatarGroupItems(
    (i) =>
      interviewers.map((interviewer) =>
        i.avatar({
          key: interviewer.id,
          name: formatPersonName(interviewer.firstName, interviewer.lastName),
          image: interviewer.imageUrl,
          onClick: () => {
            setSelectedInterviewer(interviewer);
            modalManager.open(
              ModalName.INTERVIEWER_DETAILS,
              getInterviewerDetailsDialogId(interviewer.id)
            );
          },
        })
      ),
    [interviewers, modalManager]
  );
  const interviewerNames = useMemo(
    () =>
      strings.joinWithFinalSeparator(
        interviewers.map((interviewer) => interviewer.firstName)
      ),

    [interviewers]
  );
  return (
    <section className="flex gap-[0.375rem] items-center">
      <StopPropagation>
        <AvatarGroup
          size={avatarSize}
          items={interviewerAvatars}
          className={clsx({
            "opacity-40": isInPast,
          })}
        />
      </StopPropagation>
      <p className="text-subtle text-body-md truncate">{interviewerNames}</p>
      <StopPropagation>
        <InterviewerDetailsDialog
          id={getInterviewerDetailsDialogId(selectedInterviewer?.id ?? "")}
          interviewer={selectedInterviewer}
          onClose={() => setSelectedInterviewer(undefined)}
        />
      </StopPropagation>
    </section>
  );
}

InterviewersSection.fragments = {
  interviewer: INTERVIEW_ITEM_INTERVIEWER_FRAGMENT,
};
